import { useTranslation } from 'react-i18next';
import {
  formatElapsedPlanPeriodDate,
  formatPlanPeriodDate,
} from '@/features/billing/lib/formatPlanPeriodDate';
import {
  getSubscriptionHealth,
  isSubscriptionInterrupted,
  subscriptionHealthDate,
} from '@/features/billing/lib/subscriptionHealth';
import {
  OrganizationType,
  SubscriptionPlan,
  UserTier,
  type Organization,
} from '@/types/gql.generated';
import { Flex, Text } from '@/ui';
import { InheritedAccessNote } from '../../../components/InheritedAccessNote';
import { PlanCard } from '../../../components/PlanCard';

type Props = {
  organization: Organization;
};

type Row = { label: string; value: string };

// What a non-owner sees: why this workspace matters to them, and the few facts
// they can act on. No seat counts and no roster — those are owner-only, and the
// panel deliberately skips fetching them.
export const MemberOrganizationPanel = ({ organization }: Props) => {
  const { t } = useTranslation('settingsModal', {
    keyPrefix: 'organizations',
  });

  const isFamily = organization.type === OrganizationType.Family;
  const grantsPlus = organization.seatTier === UserTier.Pro;
  const health = getSubscriptionHealth(organization);
  const isInterrupted = isSubscriptionInterrupted(health);

  const planName =
    organization.subscriptionPlan === SubscriptionPlan.Family
      ? t('plan.name_family')
      : organization.subscriptionPlan === SubscriptionPlan.Business
        ? t('plan.name_business')
        : organization.subscriptionPlan === SubscriptionPlan.Plus
          ? t('plan.name_plus')
          : null;

  // Read off the health, not the org's raw column: `none` covers a cancellation
  // that aged out of the notice window, and those rows still carry a period end —
  // so the raw column put a past date next to the "Renews" label below.
  const periodEnd = subscriptionHealthDate(health);
  const formattedDate = formatPlanPeriodDate(periodEnd);
  const endedDate = formatElapsedPlanPeriodDate(periodEnd);

  // What the period-end date means depends on the subscription. Labeling a plan
  // that has stopped granting "Renews" tells the member something untrue — and
  // that is exactly what a past-due workspace used to say, next to a date that
  // had already gone by.
  const dateLabel = isInterrupted
    ? t('member_view.ended')
    : health.state === 'ending'
      ? t('member_view.ends')
      : health.state === 'manual'
        ? t('member_view.active_through')
        : t('member_view.renews');

  // For a plan that stopped granting, only a date that has actually passed — an
  // immediate cancellation leaves the period end unreached.
  const dateValue = isInterrupted ? endedDate : formattedDate;

  const rows: Row[] = [
    ...(planName ? [{ label: t('member_view.plan'), value: planName }] : []),
    ...(dateValue ? [{ label: dateLabel, value: dateValue }] : []),
    { label: t('member_view.role'), value: t('member_view.role_member') },
  ];

  return (
    <PlanCard gap="5">
      {grantsPlus ? (
        <InheritedAccessNote
          title={t('member_view.access_title', { name: organization.name })}
          description={t(
            isFamily
              ? 'member_view.access_family'
              : 'member_view.access_business'
          )}
        />
      ) : (
        // Their Plus went away and they had no part in it. The fact table alone
        // left that unexplained, so say what happened and who can fix it — while
        // being clear nothing of theirs was taken.
        isInterrupted && (
          <InheritedAccessNote
            description={t('member_view.interrupted_body')}
            title={t(
              health.state === 'dunning'
                ? 'member_view.interrupted_title_past_due'
                : 'member_view.interrupted_title_expired',
              { name: organization.name }
            )}
          />
        )
      )}
      <Flex direction="column">
        {rows.map((row) => (
          <Flex
            borderTopColor="gray.200"
            borderTopWidth="1px"
            gap="4"
            key={row.label}
            py="3"
          >
            <Text color="customgray.mid" flexShrink="0" w="9.5rem">
              {row.label}
            </Text>
            <Text fontWeight="medium">{row.value}</Text>
          </Flex>
        ))}
      </Flex>
    </PlanCard>
  );
};
